// frontend/src/components/FinanciamentoDialog.js
// As três ações do time de Financiamentos (e do master) numa proposta de financiamento aberta: Em análise (escolhe a
// financeira do Tiny), Aprovar (a proposta vira pedido no nome do vendedor e vai ao Tiny) e Negar (volta ao vendedor).
// As regras moram no servidor (PATCH /propostas/{id}/financiamento); aqui só o formulário e os avisos.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Hourglass, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { API_URL } from '@/contexts/AuthContext';
import { formatBRL } from '@/lib/format';

// O botão de cada próxima etapa (lib/pagamento.proximasEtapas diz quais valem).
export const ACAO_FINANCIAMENTO = {
  analise: { label: 'Em análise', icon: Hourglass },
  aprovado: { label: 'Aprovar', icon: CheckCircle },
  negado: { label: 'Negar', icon: XCircle },
};

const TEXTOS = {
  analise: { titulo: 'Financiamento em análise', confirmar: 'Marcar em análise', enviando: 'Salvando' },
  aprovado: { titulo: 'Aprovar o financiamento', confirmar: 'Aprovar e criar o pedido', enviando: 'Criando o pedido' },
  negado: { titulo: 'Negar o financiamento', confirmar: 'Negar', enviando: 'Salvando' },
};

// detail de validação (422) vem em lista: o toast só recebe texto.
const textoDoErro = (e, padrao) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : padrao);

// Toast do que aconteceu; na aprovação, o número do pedido e o aviso se o Tiny falhou (o pedido fica para reenviar).
const avisar = (etapa, proposta, financeira, data) => {
  if (etapa === 'analise') {
    toast.success(`${proposta.numero_proposta} em análise na ${financeira}`);
  } else if (etapa === 'negado') {
    toast.success(`Financiamento da ${proposta.numero_proposta} negado. A proposta voltou ao vendedor.`);
  } else if (data.tiny === 'erro') {
    toast.warning(
      `Financiamento aprovado: ${data.pedido?.numero_pedido} criado no nome de ${proposta.vendedor_nome}, mas o envio ao Tiny falhou`
      + `${data.tiny_erro ? ` (${data.tiny_erro})` : ''}. O pedido fica em Erros Tiny para reenviar.`,
      { duration: 12000 },
    );
  } else {
    toast.success(
      `Financiamento aprovado: ${data.pedido?.numero_pedido} criado no nome de ${proposta.vendedor_nome} e enviado ao Tiny.`,
      { duration: 8000 },
    );
  }
};

/**
 * `acao` = { etapa: 'analise' | 'aprovado' | 'negado', proposta }. Montado com `key` por ação: os campos nascem da
 * proposta. `onConcluido(proposta | null)`: a proposta como o servidor devolveu, ou null quando ela mudou no meio (409).
 */
export default function FinanciamentoDialog({ acao, onFechar, onConcluido }) {
  const { etapa, proposta } = acao;
  const atual = proposta.financiamento || {};
  const comFinanceira = etapa !== 'negado';
  const [financeiras, setFinanceiras] = useState(null); // null = carregando
  const [erroLista, setErroLista] = useState(null);
  const [tentativa, setTentativa] = useState(0);
  // Depois de um negado a análise vai a outra financeira: a que negou não vem marcada.
  const [financeira, setFinanceira] = useState(atual.etapa === 'negado' ? '' : atual.financeira || '');
  // A condição aprovada começa com a da proposta (ou a que a análise já gravou).
  const [parcelas, setParcelas] = useState(String(atual.parcelas ?? proposta.dados?.parcelamento ?? ''));
  const [valorParcela, setValorParcela] = useState(String(atual.valor_parcela ?? proposta.dados?.valor_parcela ?? ''));
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  // A 1ª leitura de cada conta no Tiny pode levar até 30 s (depois, 10 min de cache no servidor).
  useEffect(() => {
    if (!comFinanceira) return undefined;
    let ativo = true;
    axios.get(`${API_URL}/propostas/${proposta.id}/financeiras`)
      .then(({ data }) => {
        if (!ativo) return;
        setFinanceiras(data);
        // Financeira que saiu da lista não fica escolhida; com uma só, ela já vem marcada.
        setFinanceira((f) => (data.some((x) => x.nome === f) ? f : data.length === 1 ? data[0].nome : ''));
      })
      .catch((e) => { if (ativo) setErroLista(textoDoErro(e, 'Não foi possível carregar as financeiras do Tiny agora')); });
    return () => { ativo = false; };
  }, [comFinanceira, proposta.id, tentativa]);

  const tentarDeNovo = () => {
    setErroLista(null);
    setFinanceiras(null);
    setTentativa((n) => n + 1);
  };

  const nParcelas = parseInt(parcelas, 10);
  const valido = etapa === 'negado'
    ? Boolean(motivo.trim())
    : Boolean(financeira) && (etapa === 'analise' || (nParcelas >= 1 && Number(valorParcela) > 0));

  const confirmar = async () => {
    const corpo = etapa === 'negado' ? { etapa, motivo: motivo.trim() }
      : etapa === 'analise' ? { etapa, financeira }
        : { etapa, financeira, parcelas: nParcelas, valor_parcela: Number(valorParcela) };
    setEnviando(true);
    try {
      const { data } = await axios.patch(`${API_URL}/propostas/${proposta.id}/financiamento`, corpo);
      avisar(etapa, proposta, financeira, data);
      onConcluido(data);
    } catch (e) {
      toast.error(textoDoErro(e, 'Não foi possível mudar a etapa do financiamento'));
      if (e.response?.status === 409) onConcluido(null); // a proposta mudou: a lista recarrega
      setEnviando(false);
    }
  };

  const textos = TEXTOS[etapa];
  return (
    <Dialog open onOpenChange={(aberto) => { if (!aberto && !enviando) onFechar(); }}>
      <DialogContent data-testid={`financiamento-dialog-${etapa}`}>
        <DialogHeader>
          <DialogTitle>{textos.titulo}</DialogTitle>
          <DialogDescription>
            {[proposta.numero_proposta, proposta.dados?.cliente_nome, formatBRL(proposta.totais?.total)].filter(Boolean).join(' · ')}
          </DialogDescription>
        </DialogHeader>

        {comFinanceira && (
          <div className="space-y-2">
            <Label htmlFor="financeira">Financeira</Label>
            {erroLista ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-sheet2 px-3 py-2.5 text-[13px]">
                <span className="text-red-700 dark:text-red-300">{erroLista}</span>
                <Button size="sm" variant="outline" onClick={tentarDeNovo}>Tentar de novo</Button>
              </div>
            ) : financeiras === null ? (
              <p className="flex h-9 items-center gap-2 rounded-md border border-input px-3 text-[13px] text-gray-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Carregando as financeiras do Tiny
              </p>
            ) : financeiras.length === 0 ? (
              <p className="rounded-lg bg-sheet2 px-3 py-2.5 text-[13px] text-gray-600 dark:text-gray-300">
                Nenhuma financeira habilitada no Tiny para esta proposta. Habilite a forma de recebimento no Tiny e tente de novo.
              </p>
            ) : (
              <Select value={financeira} onValueChange={(v) => v && setFinanceira(v)} disabled={enviando}>
                <SelectTrigger id="financeira" data-testid="select-financeira">
                  <SelectValue placeholder="Escolha a financeira" />
                </SelectTrigger>
                <SelectContent>
                  {financeiras.map((f) => <SelectItem key={f.nome} value={f.nome}>{f.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {etapa === 'aprovado' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="parcelas-aprovadas">Parcelas</Label>
                <Input
                  id="parcelas-aprovadas" type="number" min={1} step={1} value={parcelas} disabled={enviando}
                  onChange={(e) => setParcelas(e.target.value)} data-testid="input-parcelas-aprovadas"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valor-parcela-aprovada">Valor da parcela</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">R$</span>
                  <Input
                    id="valor-parcela-aprovada" type="number" min="0.01" step="0.01" value={valorParcela} disabled={enviando}
                    onChange={(e) => setValorParcela(e.target.value)} className="pl-9" data-testid="input-valor-parcela-aprovada"
                  />
                </div>
              </div>
            </div>
            <div className="rounded-lg bg-sheet2 px-4 py-3 text-[13px] text-gray-600 dark:text-gray-300">
              <p>O pedido nasce no nome de {proposta.vendedor_nome} e vai ao Tiny agora.</p>
              {enviando && (
                <p className="mt-1.5 flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Criando o pedido e enviando ao Tiny. Pode levar até 30 segundos.
                </p>
              )}
            </div>
          </>
        )}

        {etapa === 'negado' && (
          <div className="space-y-2">
            <Label htmlFor="motivo-negado">Motivo</Label>
            <Textarea
              id="motivo-negado" rows={3} value={motivo} disabled={enviando} autoFocus
              onChange={(e) => setMotivo(e.target.value)} placeholder="Ex: renda insuficiente, restrição no CPF"
              data-testid="input-motivo-negado"
            />
            <p className="text-[12.5px] text-gray-500">A proposta volta ao vendedor com este motivo.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onFechar} disabled={enviando}>Cancelar</Button>
          <Button
            variant={etapa === 'negado' ? 'destructive' : 'default'} onClick={confirmar} disabled={!valido || enviando}
            data-testid={`confirmar-financiamento-${etapa}`}
          >
            {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {enviando ? textos.enviando : textos.confirmar}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
